'use client';
import {
  PROXY_POOL,
  proxyPoolConfig,
  SCRAPER_REQUEST_PARAM,
} from '@decodo/utils';

import { UseFormReturn } from '@/hooks/useForm';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { getIntlText } from '@/lib/i18n/i18n.server';
import { cn } from '@/lib/utils';

import Badge from '@/components/atoms/badge';
import { Radio } from '@/components/atoms/Radio';
import { Tooltip } from '@/components/atoms/Tooltip/Tooltip';

import { useScraperTranslations } from '../scraperTranslations';

import iconInfoSrc from '~/icons/info-circle.svg';
import { SvgIcon } from '@/components/SvgIcon';

type ProxyPoolSelectorProps = {
  form: UseFormReturn<Record<string, string | boolean>>;
};

const poolOrder: PROXY_POOL[] = [PROXY_POOL.STANDARD, PROXY_POOL.PREMIUM];

export const ProxyPoolSelector = ({ form }: ProxyPoolSelectorProps) => {
  const { getTranslation } = useScraperTranslations();
  const { texts } = useTranslation();

  const currentValue =
    form.values[SCRAPER_REQUEST_PARAM.PROXY_POOL] || PROXY_POOL.PREMIUM;

  return (
    <div className='flex flex-wrap items-center gap-x-6 gap-y-3 md:gap-x-8'>
      {poolOrder.map((pool) => {
        const config = proxyPoolConfig[pool];
        const isChecked = currentValue === pool;
        const label = getTranslation(config.label.id ?? '') ?? '';
        const description = getTranslation(config.description.id ?? '') ?? '';
        const countriesCountText = getIntlText(texts.scraper.countries_count, {
          count: String(config.numOfOptions),
        });

        return (
          <div
            key={pool}
            className='flex min-w-max items-center gap-1 md:gap-2'
          >
            <Radio
              name={SCRAPER_REQUEST_PARAM.PROXY_POOL}
              value={pool}
              checked={isChecked}
              label={label}
              onChange={() =>
                form.setFieldValue(SCRAPER_REQUEST_PARAM.PROXY_POOL, pool)
              }
            />
            <Badge
              color='gray'
              variant='soft'
              classNames={{
                root: cn(
                  'px-2 py-[2px] text-center uppercase',
                  'text-[8px] font-semibold leading-[12px] tracking-[0.8px] font-serif text-gray-dark-200',
                  'md:text-label-s-medium md:font-mono md:tracking-[0.1em] md:text-gray-700 md:dark:text-gray-dark-200',
                  'bg-gray-200 dark:bg-gray-dark-700'
                ),
              }}
            >
              {countriesCountText}
            </Badge>
            {description && (
              <Tooltip
                flowDirection='bottom-center'
                classNames={{
                  tooltip: 'z-50',
                  text: 'max-w-56',
                }}
                text={description}
              >
                <SvgIcon
                  src={iconInfoSrc}
                  className='text-gray-400 hover:text-brand-500 h-4 w-4'
                />
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProxyPoolSelector;
